import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, RefreshCw } from "lucide-react";

const SOURCES = [
  { value: "top_scorers", label: "Top scorers" },
  { value: "first_places", label: "1st places" },
  { value: "aggregate", label: "Aggregate preset" },
];
const COLS = [
  ["name", "Member"], ["image_title", "Image title"], ["competition", "Competition"],
  ["place", "Place"], ["score", "Score"], ["judge", "Judge"],
];

/**
 * Right-hand data panel: choose a source (season top scorers / 1st
 * places / saved aggregate preset), load the rows, tick the ones to
 * print, click a row to preview it, and edit any cell inline.
 */
export function DataPanel({ seasons, seasonId, onSeasonId, source, onSource, rows, onRows, selected, onSelected, previewIdx, onPreviewIdx, clubName }) {
  const setSource = onSource;
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/aggregate/presets").then((r) => setPresets(r.data)).catch(() => setPresets([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { source, season_id: seasonId || undefined };
      if (source === "aggregate") params.preset_id = presetId;
      const { data } = await api.get("/graphic-studio/sources", { params });
      onRows(data.rows);
      onSelected(new Set(data.rows.map((r) => r.id)));
      onPreviewIdx(0);
      toast.success(`${data.rows.length} certificate${data.rows.length === 1 ? "" : "s"} loaded — ${data.label}`);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to load data");
    } finally { setLoading(false); }
  };

  const edit = (i, key, val) => onRows(rows.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); onSelected(n); };
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const addBlank = () => {
    const seasonName = seasons.find((s) => s.id === seasonId)?.season_name || "";
    const r = { id: `manual-${Date.now()}`, code: "", name: "", competition: "", image_title: "", score: "", total: "", rank: "", place: "", judge: "", season: seasonName, club: clubName };
    onRows([...rows, r]);
    onSelected(new Set([...selected, r.id]));
    onPreviewIdx(rows.length);
  };
  const canLoad = source === "aggregate" ? !!presetId : !!seasonId;

  return (
    <div className="space-y-3 h-full flex flex-col" data-testid="data-panel">
      <div className="grid grid-cols-3 gap-1">
        {SOURCES.map((s) => (
          <button key={s.value} onClick={() => setSource(s.value)} data-testid={`source-${s.value}`}
            className={`text-xs py-1.5 border ${source === s.value ? "border-amber-400 text-amber-400" : "border-neutral-800 muted-text"}`}>{s.label}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <select value={seasonId} onChange={(e) => onSeasonId(e.target.value)} className="ccs-input text-xs flex-1 min-w-0" data-testid="data-season-select">
          <option value="">Season…</option>
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
        </select>
        {source !== "aggregate" && (
          <button onClick={load} disabled={!canLoad || loading} className="ccs-btn text-xs flex items-center gap-1 flex-none" data-testid="data-load">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Load
          </button>
        )}
      </div>
      {source === "aggregate" && (
        <div className="flex gap-2">
          <select value={presetId} onChange={(e) => setPresetId(e.target.value)} className="ccs-input text-xs flex-1 min-w-0" data-testid="data-preset-select">
            <option value="">Preset…</option>
            {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={load} disabled={!canLoad || loading} className="ccs-btn text-xs flex items-center gap-1 flex-none" data-testid="data-load">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Load
          </button>
        </div>
      )}
      {source === "aggregate" && (
        <div className="muted-text text-[11px]" data-testid="data-aggregate-hint">Loads the aggregate <b>winner</b> only, with every image title and judge from the preset's competitions.</div>
      )}
      {source === "aggregate" && presets.length === 0 && (
        <div className="muted-text text-[11px]" data-testid="data-no-presets">No aggregate presets yet — save one from Competitions → Presets.</div>
      )}

      <div className="flex items-center justify-between text-xs">
        <span className="muted-text" data-testid="data-count">{selected.size} of {rows.length} selected</span>
        <button onClick={addBlank} className="ccs-btn-ghost text-[11px] flex items-center gap-1" data-testid="data-add-blank"><Plus size={11} /> Blank certificate</button>
      </div>

      <div className="flex-1 overflow-auto border border-neutral-800">
        <table className="ccs-table text-[11px]" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ width: 28 }}><input type="checkbox" checked={allOn} onChange={() => onSelected(allOn ? new Set() : new Set(rows.map((r) => r.id)))} data-testid="data-select-all" /></th>
              {COLS.map(([k, l]) => <th key={k}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={COLS.length + 1} className="muted-text text-center py-6" data-testid="data-empty">Load a data source or add a blank certificate.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id} onClick={() => onPreviewIdx(i)} className={i === previewIdx ? "bg-amber-500/10" : "cursor-pointer"} data-testid={`data-row-${i}`}>
                <td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} data-testid={`data-row-check-${i}`} /></td>
                {COLS.map(([k]) => (
                  <td key={k} className="p-0">
                    <textarea value={r[k] ?? ""} onChange={(e) => edit(i, k, e.target.value)} onClick={(e) => e.stopPropagation()}
                      rows={Math.max(1, String(r[k] ?? "").split("\n").length)}
                      className="bg-transparent w-full px-2 py-1 outline-none focus:bg-amber-500/10 resize-none block" style={{ minWidth: k === "place" || k === "score" ? 44 : 96 }}
                      data-testid={`data-cell-${i}-${k}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
